import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getSubmissions, deleteSubmission, setSubmissionsPage } from '../../actions/submissionActions';
import { getSubmissionFormsMinimal } from '../../actions/submissionFormActions';
import { getPrograms } from '../../actions/programActions';
import { getUsersMinimal } from '../../actions/userActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import TableView from '../content-management/views/TableView';
import MobileView from '../content-management/views/MobileView';
import TableSkeleton from '../content-management/views/TableView/TableSkeleton';
import MobileSkeleton from '../content-management/views/MobileView/MobileSkeleton';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import { Plus, Filter, X, Calendar } from 'lucide-react';
import { loadContentManagementFilters, saveContentManagementFilters } from '../../utils/contentManagementFilters';
import { isAdminRole } from '../../utils/roleHelpers';

const SubmissionsManagementContainer = ({
  auth: { user },
  submission: { submissions, loading, currentPage, totalPages, totalSubmissions, hasNextPage, hasPrevPage },
  submissionForm: { minimalSubmissionForms },
  program: { programs },
  user: { usersMinimal },
  getSubmissions,
  deleteSubmission,
  getSubmissionFormsMinimal,
  getPrograms,
  getUsersMinimal,
  setSubmissionsPage
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersLoadedRef = useRef(false);

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  // Submissions are typically admin-only, but we keep the structure flexible
  const isReadOnly = false; // Admins can edit/delete submissions

  useEffect(() => {
    // Load submission forms for filter dropdown (lightweight)
    getSubmissionFormsMinimal();
  }, [getSubmissionFormsMinimal]);

  // Lazy-load users and programs only when filter panel is opened
  const filterDataLoadedRef = useRef(false);
  useEffect(() => {
    if (showFilters && !filterDataLoadedRef.current) {
      getPrograms();
      getUsersMinimal();
      filterDataLoadedRef.current = true;
    }
  }, [showFilters, getPrograms, getUsersMinimal]);

  useEffect(() => {
    const saved = loadContentManagementFilters('Submissions');
    if (saved?.filters) {
      setSelectedStudents(saved.filters.selectedStudents || []);
      setSelectedPrograms(saved.filters.selectedPrograms || []);
      setSelectedForms(saved.filters.selectedForms || []);
      setSelectedStatuses(saved.filters.selectedStatuses || []);
      setStartDate(saved.filters.startDate || '');
      setEndDate(saved.filters.endDate || '');
      if (saved.count > 0) {
        setShowFilters(true);
      }
    }
    filtersLoadedRef.current = true;
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    // Fetch submissions with current pagination and filters
    if (!filtersHydrated) return;
    const filters = {};
    if (selectedStudents.length > 0) {
      filters.students = selectedStudents;
    }
    if (selectedPrograms.length > 0) {
      filters.programs = selectedPrograms;
    }
    if (selectedForms.length > 0) {
      filters.forms = selectedForms;
    }
    if (selectedStatuses.length > 0) {
      filters.statuses = selectedStatuses;
    }
    if (startDate) {
      filters.startDate = startDate;
    }
    if (endDate) {
      filters.endDate = endDate;
    }
    getSubmissions(currentPage, 10, filters);
  }, [filtersHydrated, currentPage, selectedStudents, selectedPrograms, selectedForms, selectedStatuses, startDate, endDate, getSubmissions]);

  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    const count = selectedStudents.length + selectedPrograms.length + selectedForms.length + selectedStatuses.length +
      (startDate ? 1 : 0) + (endDate ? 1 : 0);
    saveContentManagementFilters('Submissions', {
      selectedStudents,
      selectedPrograms,
      selectedForms,
      selectedStatuses,
      startDate,
      endDate
    }, count);
  }, [selectedStudents, selectedPrograms, selectedForms, selectedStatuses, startDate, endDate]);

  const handleView = (item) => {
    navigate(`/content-management/submissions/${item._id}/view`);
  };

  const handleEdit = (item) => {
    navigate(`/content-management/submissions/${item._id}/edit`);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    deleteSubmission(itemToDelete._id);
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Submissions') {
      // Already on submissions page, do nothing
      return;
    }
    // Navigate to content management with selected tab
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  const getItemTitle = (item) => {
    const firstName = item.userId?.name || '';
    const lastName = item.userId?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Unknown Student';
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

    const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (thumbnail.url) return thumbnail.url;
    if (thumbnail.data && thumbnail.contentType) return `data:${thumbnail.contentType};base64,${thumbnail.data}`;
    return null;
  };

  // Handle pagination - only update Redux state, useEffect will fetch data
  const handlePageChange = (newPage) => {
    setSubmissionsPage(newPage);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearAllFilters = () => {
    setSelectedStudents([]);
    setSelectedPrograms([]);
    setSelectedForms([]);
    setSelectedStatuses([]);
    setStartDate('');
    setEndDate('');
    setSubmissionsPage(1);
  };

  const hasActiveFilters = selectedStudents.length > 0 || selectedPrograms.length > 0 || 
    selectedForms.length > 0 || selectedStatuses.length > 0 || startDate || endDate;

  // Get unique values from submissions
  const uniqueStudents = usersMinimal && usersMinimal.length > 0
    ? usersMinimal.map(user => ({
        _id: user._id,
        title: `${user.name || ''} ${user.lastName || ''}`.trim() || 'Unknown Student'
      }))
    : [];
    
  const uniquePrograms = programs && programs.length > 0
    ? programs
    : [];
    
  const uniqueForms = minimalSubmissionForms && minimalSubmissionForms.length > 0
    ? minimalSubmissionForms
    : [];
    
  const uniqueStatuses = ['pending', 'approved', 'rejected', 'more_info_needed'];

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Submissions"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-[#054581]">
                  Manage Submissions
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    hasActiveFilters
                      ? 'bg-[#1086a4] text-white shadow-md'
                      : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                  }`}
                >
                  <Filter size={18} />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {hasActiveFilters && (
                    <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                      Active
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-[#054581] hover:underline flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear All
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MultiSelectSearchFilter
                    items={uniqueStudents}
                    selectedItems={selectedStudents}
                    onSelectionChange={(selected) => {
                      setSelectedStudents(selected);
                      setSubmissionsPage(1);
                    }}
                    title="Students"
                    placeholder="Search students..."
                    emptyMessage="No students available"
                  />

                  <MultiSelectSearchFilter
                    items={uniquePrograms.map(program => ({ _id: program._id, title: program.title }))}
                    selectedItems={selectedPrograms}
                    onSelectionChange={(selected) => {
                      setSelectedPrograms(selected);
                      setSubmissionsPage(1);
                    }}
                    title="Programs"
                    placeholder="Search programs..."
                    emptyMessage="No programs available"
                  />

                  <MultiSelectSearchFilter
                    items={uniqueForms.map(form => ({ _id: form._id, title: form.title }))}
                    selectedItems={selectedForms}
                    onSelectionChange={(selected) => {
                      setSelectedForms(selected);
                      setSubmissionsPage(1);
                    }}
                    title="Forms"
                    placeholder="Search forms..."
                    emptyMessage="No forms available"
                  />

                  <MultiSelectSearchFilter
                    items={uniqueStatuses.map(status => ({ _id: status, title: status === 'more_info_needed' ? 'More Info Needed' : status.charAt(0).toUpperCase() + status.slice(1) }))}
                    selectedItems={selectedStatuses}
                    onSelectionChange={(selected) => {
                      setSelectedStatuses(selected);
                      setSubmissionsPage(1);
                    }}
                    title="Statuses"
                    placeholder="Search statuses..."
                    emptyMessage="No statuses available"
                  />
                </div>

                {/* Submitted Date Range Filter */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#1086a4]" />
                    <span className="text-sm font-medium text-[#054581]">Submitted Date Range</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">From</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setSubmissionsPage(1);
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">To</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setSubmissionsPage(1);
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <>
                {/* Desktop Skeleton */}
                <div className="hidden md:block">
                  <TableSkeleton rowCount={10} activeTab="Submissions" />
                </div>

                {/* Mobile Skeleton */}
                <div className="md:hidden">
                  <MobileSkeleton cardCount={10} />
                </div>
              </>
            ) : (
              <>
                {/* Mobile View */}
                <div className="md:hidden">
                  <MobileView
                    items={submissions}
                    activeTab="Submissions"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={isReadOnly}
                  />
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                  <TableView
                    items={submissions}
                    activeTab="Submissions"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={isReadOnly}
                  />
                </div>

                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalSubmissions}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  itemName="submissions"
                />
              </>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={showDeleteConfirmation}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
              itemType="Submission"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  submission: state.submission,
  submissionForm: state.submissionForm,
  program: state.program,
  user: state.user
});

export default connect(
  mapStateToProps,
  {
    getSubmissions,
    deleteSubmission,
    getSubmissionFormsMinimal,
    getPrograms,
    getUsersMinimal,
    setSubmissionsPage
  }
)(SubmissionsManagementContainer);
